class SemiHeavyDutyTruck extends HTMLElement {
    constructor() {
        super();
        this.SemiHeavyDutyTruckJsonData = null;
        this.uniqueMakeModels = [];
        this.matchingUniqueYears = [];
        this.elements = {};
        this.selectedData = {
            make: '',
            model: '',
            year: ''
        };
        this.initJsonData();
    }

    async initJsonData() {
        try {
            const json = await vehicleUtility.tsvSemiAndHeavyTrucksJSON();
            this.initVehicle(json);
        } catch (error) {
            console.error('Failed to load vehicle data:', error);
        }
    }

    initVehicle(json) {
        this.SemiHeavyDutyTruckJsonData = json;
        this.uniqueMakeModels = [...new Set(Object.values(json)
            .map(item => `${item.make} | ${item.model}`))];
        this.cacheElements();
        this.bindEvents();

        const limitedSuggestions = this.uniqueMakeModels.slice(0, 3);

        const container = this.querySelector("[data-vehicle-suggestion]");
        const suggestionBox = this.querySelector(`[data-vehicle-suggestion-box]`);

        if (!container || !suggestionBox) return;

        container.innerHTML = '';

        limitedSuggestions.forEach(item => {
            const formattedText = item.replace(/\s?\|\s?/g, ' ');
            
            const li = this.createElement('li', formattedText, {
                'data-vehicle-value': '',
                'data-vehicle-model-value': item,
                class: 'block__vehicle-lookup-input-result-li'
            });

            li.addEventListener('click', (e) => {
                container.querySelectorAll("li").forEach((list) => {
                    list.classList.remove("selected");
                });
                e.target.classList.add("selected");
                let targetMakeModel = e.target.getAttribute("data-vehicle-model-value");
                var makeModelValue = e.target.textContent;

                const [make, model] = targetMakeModel.split('|').map(v => v.trim());
                this.selectedData.make = make;
                this.selectedData.model = model;
                this.matchingUniqueYears = this.getUniqueYears(make, model);

                e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-make-model]').value = makeModelValue;
                e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-submit]').classList.remove("d-none");
            });
            container.appendChild(li);
        });
    }

    cacheElements() {
        const selectors = {
            makeModelInput: '[data-search-vehicle-make-model]',
            makeModelList: '[data-search-vehicle-make-model-list]',
            makeModelBox: '[data-selected-vehicle-make-model]',
            submitButton: '[data-search-vehicle-submit]',
            yearInput: '[data-search-vehicle-year]',
            yearList: '[data-search-vehicle-year-list]',
            yearBox: '[data-selected-vehicle-year]',
            yearSubmitButton: '[data-search-vehicle-year-submit]'
        };

        Object.entries(selectors).forEach(([key, selector]) => {
            this.elements[key] = this.querySelector(selector);
        });
    }

    bindEvents() {
        if (!this.elements.makeModelInput) return;

        // Search configurations
        const searchConfigs = [
            {
                input: this.elements.makeModelInput,
                list: this.elements.makeModelList,
                box: this.elements.makeModelBox,
                submit: this.elements.submitButton,
                type: 'makeModel',
                minLength: 3,
                attribute: 'data-vehicle-model-value'
            },
            {
                input: this.elements.yearInput,
                list: this.elements.yearList,
                box: this.elements.yearBox,
                submit: this.elements.yearSubmitButton,
                type: 'year',
                minLength: 3,
                attribute: 'data-vehicle-year-value'
            }
        ];

        // Bind search events
        searchConfigs.forEach(config => this.bindSearchConfig(config));
        
        // Bind step back events
        this.bindStepBackEvents();
    }

    bindSearchConfig(config) {
        if (!config.input) return;

        let debounceTimer;
        
        // Search input handler
        config.input.addEventListener('keyup', (e) => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => this.handleSearch(e, config), 150);
        });

        // Selection handler
        config.list?.addEventListener('click', (e) => this.handleSelection(e, config));
        
        // Submit handler
        config.submit?.addEventListener('click', (e) => this.handleSubmit(e, config));
    }

    bindStepBackEvents() {
        document.addEventListener('click', (e) => {
            const backButton = e.target.closest('[data-step-back-btn]');
            if (!backButton) return;

            e.preventDefault();
            const backStep = backButton.getAttribute('data-back-step');
            
            if (backStep && backStep !== '0') {
                this.navigateToPreviousStep(backButton, backStep);
            } else {
                backButton.closest('.block__vehicle-lookup-popup').classList.add('d-none');
            }
        });
    }

    navigateToPreviousStep(button, backStep) {
        const currentStep = button.closest('.block__vehicle-lookup-popup-details');
        const parentEle = currentStep.closest('.block__vehicle-lookup-popup-list');
        
        parentEle.querySelectorAll('.block__vehicle-lookup-popup-details')
            .forEach(step => step.classList.add('d-none'));
            
        const previousStep = parentEle.querySelector(`[current-step="${backStep}"]`);
        previousStep?.classList.remove('d-none');
    }

    handleSearch(e, config) {
        const query = e.target.value.toLowerCase().replace(/\s+/g, ' ').trim();
        const inputBox = e.target.closest(".block__vehicle-lookup-input-box");
        
        // Toggle active state
        inputBox.classList.toggle("active-value", query !== '');
        
        // Reset submit button and clear results
        config.submit?.classList.add('d-none');
        config.list.innerHTML = '';

        if (!query) {
            config.box?.classList.add('d-none');
            return;
        }

        if (query.length >= config.minLength) {
            const matches = this.getMatches(config.type, query);
            this.populateResults(config, matches);
        } else {
            this.showMinLengthMessage(config);
        }
    }

    getMatches(type, query) {
        if (type === 'makeModel') {
            return this.uniqueMakeModels.filter(item => {
                const normalizedItem = item.toLowerCase()
                    .replace(/\s*\|\s*/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();
                return normalizedItem.includes(query);
            });
        }
        
        if (type === 'year') {
            return this.matchingUniqueYears.filter(year => 
                String(year).toLowerCase().includes(query));
        }
        
        return [];
    }

    handleSelection(e, config) {
        const target = e.target;
        if (!target.matches(`[${config.attribute}]`)) return;

        // Remove active state from input box
        e.target.closest(".block__vehicle-lookup-input-box").classList.remove("active-value");

        if (config.type === 'makeModel') {
            const selectedValue = target.getAttribute(config.attribute).trim();
            const [selectedMake, selectedModel] = selectedValue.split('|').map(val => val.trim());
            
            this.selectedData.make = selectedMake;
            this.selectedData.model = selectedModel;
            this.matchingUniqueYears = this.getUniqueYears(selectedMake, selectedModel);

            this.updateInputAndUI(target, `${selectedMake} ${selectedModel}`, config);
        } else if (config.type === 'year') {
            const selectedYear = target.getAttribute(config.attribute)?.trim();
            this.selectedData.year = selectedYear;
            this.updateInputAndUI(target, selectedYear, config);
        }
    }

    handleSubmit(e, config) {
        e.preventDefault();
        
        if (config.type === 'makeModel') {
            const inputValue = this.elements.makeModelInput.value;
            this.navigateToNextStep(e.target);
            this.updateFinalDisplay('[data-show-final-selected-vehicle]', inputValue);

            let make = this.selectedData.make;
            let model = this.selectedData.model
            const matchingYear = this.getMatchingOptions('year', { make, model }).slice(0, 5);

            if (matchingYear.length > 0) {
                this.navigateToNextStep(e.target);
                this.populateSuggestions('[data-year-suggestion]', matchingYear, 'year');
            }
        } else if (config.type === 'year') {
            const { make, model, year } = this.selectedData;
            this.finalizeVehicleSelection(year, make, model);
        }
    }

    populateSuggestions(selector, items, type) {
        const container = this.querySelector(selector);
        const suggestionBox = this.querySelector(`[data-${type}-suggestion-box]`);
        
        if (!container || !suggestionBox) return;
        
        container.innerHTML = '';
        
        items.forEach(item => {
            const li = this.createElement('li', item, {
                [`data-${type}-value`]: '',
                'data-vehicle-year-value': this.selectedData.year,
                'data-vehicle-make-model': `${this.selectedData.make} | ${this.selectedData.model}`,
                [`data-vehicle-${type}-value`]: item,
                class: 'block__vehicle-lookup-input-result-li'
            });

            if (type === 'year') {
                li.addEventListener('click', (e) => {
                    container.querySelectorAll("li").forEach((list) => {
                        list.classList.remove("selected");
                    });
                    e.target.classList.add("selected");
                    var YearValue = e.target.textContent;

                    this.selectedData.year = YearValue;

                    e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-year]').value = YearValue;
                    e.target.closest(".block__vehicle-lookup-input-box").querySelector('[data-search-vehicle-year-submit]').classList.remove("d-none");
                });
            }
                        
            container.appendChild(li);
        });
    }

    getMatchingOptions(field, filters) {
        const vehicleArray = Object.values(this.SemiHeavyDutyTruckJsonData);
        const fieldMap = { year: 'year' };
        const actualField = fieldMap[field] || field;
        
        const matching = vehicleArray
            .filter(v => 
                Object.entries(filters).every(([key, value]) => {
                    const vehicleValue = (key === 'year' || key === 'Year') ? v.year : v[key];
                    return vehicleValue === value;
                })
            )
            .map(v => v[actualField])
            .filter(value => value && value.trim() !== '');

        const unique = [...new Set(matching)];
        
        return actualField === 'year'
            ? unique.sort((a, b) => Number(b) - Number(a)) // newest first
            : unique;
    }


    getUniqueYears(make, model) {
        const vehicleArray = Object.values(this.SemiHeavyDutyTruckJsonData);
        const matchingYears = vehicleArray
            .filter(v => v.make === make && v.model === model)
            .map(v => v.year);

        return [...new Set(matchingYears)]
            .sort((a, b) => Number(b) - Number(a)); // newest first
    }


    populateResults(config, matches) {
        config.box?.classList.remove('d-none');
        
        if (matches.length > 0) {
            if (config.type === 'makeModel') {
                config.list.innerHTML = matches.map(match => 
                    `<li class="data-vehicle-model-list" data-vehicle-model-value="${match}" data-vehicle-value="">${match.replace(/\s*\|\s*/g, ' ')}</li>`
                ).join('');
            } else if (config.type === 'year') {
                config.list.innerHTML = matches.map(year => 
                    `<li class="data-vehicle-model-list" data-year-value="" data-vehicle-make-model="${this.selectedData.make} | ${this.selectedData.model}" data-vehicle-year-value="${year}">${year}</li>`
                ).join('');
            }
        } else {
            config.list.innerHTML = '<li class="data-vehicle-model-list">No results found</li>';
        }
    }

    showMinLengthMessage(config) {
        config.box?.classList.remove('d-none');
        const message = config.type === 'makeModel' 
            ? 'Please enter at least 3 characters of your vehicle.'
            : 'Please enter at least 3 numbers of the year.';
        config.list.innerHTML = `<li class="data-vehicle-model-list">${message}</li>`;
    }

    updateInputAndUI(targetElement, value, config) {
        // Update input value
        if (config.input) config.input.value = value;
        
        // Show submit button and hide dropdown
        config.submit?.classList.remove('d-none');
        config.box?.classList.add('d-none');

        // Update selection state
        const container = targetElement.closest('.block__vehicle-lookup-input-box');
        container?.querySelectorAll('li').forEach(li => li.classList.remove('selected'));
        targetElement.classList.add('selected');
    }

    navigateToNextStep(element) {
        const currentStep = element.closest('.block__vehicle-lookup-popup-details');
        const nextStepIndex = currentStep.getAttribute('data-next-step');
        const parentEle = currentStep.closest('.block__vehicle-lookup-popup-list');
        
        parentEle.querySelectorAll('.block__vehicle-lookup-popup-details')
            .forEach(step => step.classList.add('d-none'));
        
        const nextStep = parentEle.querySelector(`[current-step="${nextStepIndex}"]`);
        nextStep?.classList.remove('d-none');
    }

    updateFinalDisplay(selector, content) {
        const element = this.querySelector(selector);
        if (element) element.innerHTML = content;
    }

    finalizeVehicleSelection(year, make, model) {
        document.querySelector(".block__vehicle-lookup-popup:not(.d-none) .block__vehicle-lookup-popup-details:not(.d-none)").querySelector(".block__vehicle-lookup-input-button").classList.add("loading");
        
        // Always create/update vehicle data to ensure it's set as default and currentSelectedData is updated
        const vehicleData = this.createVehicleData(year, make, model);
        this.addVehicleToSelection(vehicleData);

        const partNumbers = this.getPartNumbers(year, make, model);
        this.redirectToResults(partNumbers);
    }

    checkIfVehicleExists(year, make, model) {
        return window.customerSelectedVehicles.sahdt.some(vehicle =>
            vehicle.year === year &&
            vehicle.make === make &&
            vehicle.model === model
        );
    }

    createElement(tag, textContent, attributes = {}) {
        const element = document.createElement(tag);
        if (textContent) element.textContent = textContent;
        
        Object.entries(attributes).forEach(([key, value]) => {
            if (key === 'class') {
                element.className = value;
            } else {
                element.setAttribute(key, value);
            }
        });
        
        return element;
    }

    createVehicleData(year, make, model) {
        return {
            id: Math.floor(Math.random() * 4000) + 1000,
            year,
            make,
            model,
            default: false
        };
    }

    resetAllVehicleDefaults() {
        const storageKeys = [
            'vehicle_data_side',
            'vehicle_data_sahdt',
            'vehicle_data',
            'turf_data',
            'heavy_equipment_data'
        ];

        storageKeys.forEach(key => {
            try {
                const storedData = localStorage.getItem(key);
                if (storedData) {
                    const vehicles = JSON.parse(storedData);
                    if (Array.isArray(vehicles)) {
                        vehicles.forEach(vehicle => {
                            vehicle.default = false;
                        });
                        localStorage.setItem(key, JSON.stringify(vehicles));
                    }
                }
            } catch (error) {
                console.error(`Error updating ${key}:`, error);
            }
        });
    }

    addVehicleToSelection(vehicleData) {
        const isCustomer = document.body.classList.contains('customer-loggedin');
        
        // Reset all vehicle defaults to false across all storage keys
        this.resetAllVehicleDefaults();
        
        // Set the new vehicle as default: true
        vehicleData.default = true;
        
        if (!window.customerSelectedVehicles) {
            window.customerSelectedVehicles = { sahdt: [] };
        }
        if (!window.customerSelectedVehicles.sahdt) {
            window.customerSelectedVehicles.sahdt = [];
        }

        // Check if vehicle already exists, if so update it, otherwise add it
        const existingIndex = window.customerSelectedVehicles.sahdt.findIndex(
            v => v.year === vehicleData.year && 
                 v.make === vehicleData.make && 
                 v.model === vehicleData.model
        );

        if (existingIndex !== -1) {
            window.customerSelectedVehicles.sahdt[existingIndex] = vehicleData;
        } else {
            window.customerSelectedVehicles.sahdt.push(vehicleData);
        }
        
        // Update currentSelectedData and currentSelectedDataLabels with latest vehicle
        localStorage.setItem('currentSelectedData', JSON.stringify(vehicleData));
        localStorage.setItem('currentSelectedDataLabels', JSON.stringify(vehicleData));

        if (isCustomer && window.shopifyFormUtility) {
            localStorage.removeItem('vehicle_data_sahdt');
        } else {
            localStorage.setItem('vehicle_data_sahdt', JSON.stringify(window.customerSelectedVehicles.sahdt));
        }
    }

    getPartNumbers(year, make, model) {
        const vehicleArray = Object.values(this.SemiHeavyDutyTruckJsonData);
        const filteredVehicles = vehicleArray.filter(item => {
            const matchesCriteria = [
                String(item.year || '').toLowerCase() === String(year || '').toLowerCase(),
                String(item.make || '').toLowerCase() === String(make || '').toLowerCase(),
                String(item.model || '').toLowerCase() === String(model || '').toLowerCase()
            ];
            return matchesCriteria.every(Boolean);
        });

        const uniquePartNumbers = [...new Set(filteredVehicles.map(item => item.part_number))];
        return uniquePartNumbers.map(p => `sku-${p}`).join(',');
    }

    redirectToResults(partNumbers) {
        const isCustomer = document.body.classList.contains('customer-loggedin');
        
        if (isCustomer) {
            shopifyFormUtility._updateCustomerSHeavyMeta(window.customerSelectedVehicles.sahdt, partNumbers);
        } else {
            window.location.href = `/collections/all?filter.p.tag=${partNumbers}`;
        }
    }
}

customElements.define('semis-heavy-duty-trucks-shop', SemiHeavyDutyTruck);